/**
 * 消息相关工具函数
 */
import { ElMessageBox } from 'element-plus'

/**
 * 格式化消息内容，支持 <span color='red'> 标签
 * 将 color 属性转换为 style 属性
 * @param {string} content - 消息内容
 * @returns {string} 格式化后的消息内容
 */
export function formatMessageContent(content) {
  if (!content) return ''
  // 将 <span color='red'> 或 <span color="red"> 转换为 <span style="color: red">
  return content
    .replace(/<span\s+color=['"]([^'"]+)['"]/gi, '<span style="color: $1"')
    .replace(/<span\s+color=([^\s>]+)/gi, (match, color) => {
      // 处理没有引号的 color 属性
      const cleanColor = color.replace(/['">]/g, '')
      return `<span style="color: ${cleanColor}"`
    })
}

/**
 * 增强确认对话框（带统一样式）
 * @param {string} message - 提示消息
 * @param {string} title - 标题，默认为'提示'
 * @param {string} confirmButtonText - 确认按钮文本，默认为'确定'
 * @param {string} cancelButtonText - 取消按钮文本，默认为'取消'
 * @returns {Promise<boolean>} 用户确认时返回 true，取消/关闭返回 false
 */
export async function showEnhancedConfirmDialog(
  message,
  title = '提示',
  confirmButtonText = '确定',
  cancelButtonText = '取消',
  options = {}
) {
  const { width, ...messageBoxOptions } = options || {}
  const root = typeof document !== 'undefined' ? document.documentElement : null
  const varName = '--enhanced-confirm-dialog-width'
  const prevWidth = root ? root.style.getPropertyValue(varName) : ''
  const widthValue = (typeof width === 'number' ? `${width}px` : (width || '')).trim()
  const restoreWidth = () => {
    if (!root || !widthValue) return
    if (prevWidth) root.style.setProperty(varName, prevWidth)
    else root.style.removeProperty(varName)
  }

  try {
    if (root && widthValue) {
      root.style.setProperty(varName, widthValue)
    }
    await ElMessageBox.confirm(message, title, {
      confirmButtonText,
      cancelButtonText,
      customClass: 'custom-enhanced-confirm-dialog',
      closeOnClickModal: false,
      lockScroll: false,
      ...messageBoxOptions
    })
    return true
  } catch (error) {
    if (error === 'cancel') {
      return false
    }
    throw error
  } finally {
    // 关闭时 ElMessageBox 有动画，立刻还原宽度会导致“先变窄再关闭”的闪动
    // 延后还原，避免动画过程中样式突变
    if (root && widthValue) {
      setTimeout(restoreWidth, 260)
    }
  }
}

/**
 * 增强提示框（仅「确定」，样式与 showEnhancedConfirmDialog 一致，见 App.vue .custom-enhanced-confirm-dialog）
 * @param {string} message
 * @param {string} title
 * @param {string} confirmButtonText
 * @returns {Promise<void>} 点关闭/X 等也会 resolve，避免未捕获 reject
 */
export function showEnhancedAlertDialog(
  message,
  title = '提示',
  confirmButtonText = '确定',
  options = {}
) {
  const { customClass, ...restOptions } = options || {}
  
  return ElMessageBox.alert(message, title, {
    confirmButtonText,
    customClass: customClass ? `custom-enhanced-confirm-dialog ${customClass}` : 'custom-enhanced-confirm-dialog',
    closeOnClickModal: false,
    lockScroll: false,
    ...restOptions
  }).catch(() => {})
}

